// Global Instructions Rule Applied!
//
// Phase 4b — PreviewPane.
//
// Right-column preview for the email Compose screen. Shows the actual
// message that will be sent for the currently-selected recipient or team:
//   - subject and body substituted with merge values
//   - per-team override applied in team mode (the dispatch reads from the
//     same representative recipient we render against here)
//   - substituted spans highlighted so the user sees which parts are
//     dynamic
//
// Selection shape is intentionally simple: a discriminated union the
// parent passes down. PreviewPane doesn't know about staged recipient
// fetching, hooks, or the campaign-form state — it only renders.
//
// Substitution mirrors the dispatch edge function's substituteMergeFields
// + the per-mode merge_data shape (see supabase/functions/email-dispatch/
// index.ts buildIndividualPrepared / buildTeamPrepared). Keep this in
// lockstep with that file — they're the two consumers of the same
// placeholder vocabulary.
//
// Rendering surface (revised 2026-05-29):
// Wraps the body in the same thin shell render.ts uses, then renders
// inside an <iframe srcDoc>. The iframe gives a separate document /
// CSS scope / viewport — much closer to how an inbox client renders
// the outbound HTML than the previous shadow-DOM approach (which
// rendered the body without any shell at all).
//
// MSO_HEAD + the shell structure are intentionally duplicated from
// supabase/functions/email-dispatch/emails/render.ts so the preview
// and the dispatch construct the same envelope. If the two drift
// (operators report preview-vs-inbox differences), extract to a
// shared TS module imported by both sides.

import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Card, Empty, Space, Tag, Typography } from 'antd'
import type { EmailRecipientGridModel } from '../../../state_manager/data_models/email_recipient_grid_model.ts'

const { Text } = Typography

// MSO conditional + Outlook resets — DUPLICATED from render.ts so the
// preview iframe wraps the body in the same envelope the dispatch
// emits. Keep verbatim; if it drifts, extract to a shared module.
const PREVIEW_MSO_HEAD = `
<!--[if !mso]><!-->
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
<!--<![endif]-->
<!--[if mso]>
<style type="text/css">
  table, td { mso-table-lspace: 0pt !important; mso-table-rspace: 0pt !important; mso-line-height-rule: exactly; }
</style>
<xml>
  <o:OfficeDocumentSettings>
    <o:AllowPNG/>
    <o:PixelsPerInch>96</o:PixelsPerInch>
  </o:OfficeDocumentSettings>
</xml>
<![endif]-->
`.trim()

/**
 * Wrap a sanitized body HTML in the thin dispatch shell. Mirrors
 * supabase/functions/email-dispatch/emails/render.ts: doctype + html
 * lang + minimal head (charset, viewport, x-apple, title, MSO_HEAD) +
 * a body with inline `margin:0; padding:0` + a default sans-serif font
 * (a sensible default the operator's / user-applied fonts still override —
 * without it, un-fonted text falls back to the email client's Times New
 * Roman, which mismatched the editor). No Container, no Section, no forced
 * link colour. MUST mirror email-dispatch/emails/render.ts.
 *
 * For `bodySource='imported'` the body is already a full HTML document
 * — return it verbatim (Phase 1 / Stripo import will pass this through
 * once `body_source` lands on email_campaigns).
 */
function wrapInThinShell(
  bodyHtml:   string,
  subject:    string,
  bodySource: 'jodit' | 'imported' | 'ckeditor'
): string {
  if (bodySource === 'imported') return bodyHtml || ''
  return `<!doctype html><html lang="en"><head>` +
    `<title>${escapeHtml(subject || '')}</title>` +
    `<meta charset="utf-8" />` +
    `<meta name="viewport" content="width=device-width, initial-scale=1" />` +
    `<meta name="x-apple-disable-message-reformatting" />` +
    PREVIEW_MSO_HEAD +
    `</head><body style="margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif">` +
    (bodyHtml || '') +
    `</body></html>`
}

export type PreviewSelection =
  | null
  | { kind: 'individual'; recipient: EmailRecipientGridModel }
  | {
      kind: 'team'
      accountId:        string
      accountName:      string
      accountTeamName:  string                          // accounts.team_name; '' if blank
      recipients:       EmailRecipientGridModel[]      // all rows in the team (to + cc)
    }

interface PreviewPaneProps {
  selection:        PreviewSelection
  campaignSubject:  string
  campaignBodyHtml: string
  // Forward-compat: lands on email_campaigns with the Stripo import
  // session. PreviewPane already handles 'imported' (no shell wrap)
  // so the form can flip the flag with no extra work here.
  bodySource?:      'jodit' | 'imported' | 'ckeditor'
  // Campaign-level Cc / Bcc copy recipients (added to EVERY message). Shown in
  // the header strip: campaignCc merges with any per-recipient/team Cc; Bcc is
  // campaign-level only. Plain email-address arrays — the dispatch injects the
  // same addresses (edge fn step 9b).
  campaignCc?:      string[]
  campaignBcc?:     string[]
}

/**
 * Compute the team-level {{first_names_list}} value the same way the edge
 * function does — comma-join the first_names of 'to' recipients, dropping
 * blanks. Kept here too so the in-app preview matches the dispatched
 * message exactly.
 */
function firstNamesListOf(recipients: EmailRecipientGridModel[]): string {
  return recipients
    .filter((r) => r.email_recipients__role === 'to')
    .map((r) => {
      const md = r.email_recipients__merge_data
      if (md && typeof md === 'object' && !Array.isArray(md)) {
        const v = (md as Record<string, unknown>).first_name
        return v === null || v === undefined ? '' : String(v).trim()
      }
      return ''
    })
    .filter((s) => s !== '')
    .join(', ')
}

/**
 * Substitute {{key}} placeholders with the value from `data`, wrapping
 * each substituted value in a span so the preview surfaces which parts
 * are dynamic. Empty substitutions render a small marker like
 * "[first_name (empty)]" so the user can tell a placeholder existed —
 * easy to miss otherwise in team mode where {{first_name}} is empty.
 *
 * The span styles are inline (no CSS class) so the rendered HTML works
 * inside the iframe (which doesn't inherit the app stylesheets).
 */
function substituteAndHighlight(template: string, data: Record<string, unknown>): string {
  if (!template) return ''
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key: string) => {
    const v = data[key]
    const value = v === null || v === undefined ? '' : String(v)
    const titleAttr = `title="{{${key}}}"`
    if (value === '') {
      return `<span style="background:#fff1f0;color:#a8071a;padding:0 4px;border-radius:2px;font-size:0.85em;border:1px dashed #ffa39e;" ${titleAttr}>${key} (empty)</span>`
    }
    return `<span style="background:#fffbe6;color:#874d00;padding:0 2px;border-radius:2px;border:1px solid #ffe58f;" ${titleAttr}>${escapeHtml(value)}</span>`
  })
}

/** Plain-text {{key}} substitution for the subject line (no HTML markup). */
function substitutePlain(template: string, data: Record<string, unknown>): string {
  if (!template) return ''
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key: string) => {
    const v = data[key]
    return v === null || v === undefined ? '' : String(v)
  })
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export default function PreviewPane({
  selection,
  campaignSubject,
  campaignBodyHtml,
  bodySource = 'jodit',
  campaignCc = [],
  campaignBcc = []
}: PreviewPaneProps) {
  // iframe auto-resize: read body scrollHeight after each load (every
  // srcDoc change triggers a load). 200px floor so an empty body still
  // shows a visible canvas; 4px buffer absorbs the body's own bottom
  // margin / border.
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeHeight, setIframeHeight] = useState<number>(300)
  const handleIframeLoad = useCallback(() => {
    const f = iframeRef.current
    const doc = f?.contentDocument
    if (!f || !doc || !doc.body) return
    const h = doc.body.scrollHeight
    setIframeHeight(Math.max(200, h + 4))
  }, [])
  // Build the merge data + the effective subject/body the dispatch would
  // use for this selection. Memoized on the inputs so a re-render that
  // didn't change the selection skips the work.
  const computed = useMemo(() => {
    if (!selection) return null

    if (selection.kind === 'individual') {
      const r = selection.recipient
      const baseMerge =
        r.email_recipients__merge_data &&
        typeof r.email_recipients__merge_data === 'object' &&
        !Array.isArray(r.email_recipients__merge_data)
          ? (r.email_recipients__merge_data as Record<string, unknown>)
          : {}
      // Mirror the dispatch's individual-mode enrichment: team_name +
      // first_names_list also work in individual mode.
      const teamName =
        (typeof baseMerge.account_team_name === 'string' && baseMerge.account_team_name) ||
        (typeof baseMerge.account_name === 'string' && baseMerge.account_name) ||
        ''
      const firstName =
        baseMerge.first_name === null || baseMerge.first_name === undefined
          ? ''
          : String(baseMerge.first_name)
      const data: Record<string, unknown> = {
        ...baseMerge,
        team_name:        teamName,
        first_names_list: firstName
      }
      const effectiveSubject =
        (r.email_recipients__subject_override ?? '') || campaignSubject || ''
      const effectiveBody =
        (r.email_recipients__body_override_html ?? '') || campaignBodyHtml || ''
      return {
        toAddrs:        [r.email_recipients__email_address],
        ccAddrs:        [],
        subject:        substitutePlain(effectiveSubject, data),
        bodyHtml:       substituteAndHighlight(effectiveBody, data),
        overrideActive: !!r.email_recipients__has_subject_override || !!r.email_recipients__has_body_override
      }
    }

    // Team mode: mirror the dispatch's buildTeamPrepared exactly.
    const recs = selection.recipients
    const sorted = [...recs].sort((a, b) => {
      if (a.email_recipients__role !== b.email_recipients__role) {
        return a.email_recipients__role === 'to' ? -1 : 1
      }
      return (a.email_recipients__email_address || '').localeCompare(
        b.email_recipients__email_address || ''
      )
    })
    const toRows = sorted.filter((r) => r.email_recipients__role === 'to')
    const ccRows = sorted.filter((r) => r.email_recipients__role === 'cc')
    const rep    = toRows[0] ?? sorted[0]
    const repMerge =
      rep &&
      rep.email_recipients__merge_data &&
      typeof rep.email_recipients__merge_data === 'object' &&
      !Array.isArray(rep.email_recipients__merge_data)
        ? (rep.email_recipients__merge_data as Record<string, unknown>)
        : {}
    const teamName = (selection.accountTeamName || '').trim() || selection.accountName
    const data: Record<string, unknown> = {
      ...repMerge,
      first_name:       '',
      last_name:        '',
      email:            '',
      team_name:        teamName,
      first_names_list: firstNamesListOf(recs),
      account_name:     selection.accountName
    }
    const overrideSubject = rep?.email_recipients__subject_override ?? null
    const overrideBody    = rep?.email_recipients__body_override_html ?? null
    const effectiveSubject = overrideSubject || campaignSubject || ''
    const effectiveBody    = overrideBody    || campaignBodyHtml || ''
    return {
      toAddrs:        toRows.map((r) => r.email_recipients__email_address),
      ccAddrs:        ccRows.map((r) => r.email_recipients__email_address),
      subject:        substitutePlain(effectiveSubject, data),
      bodyHtml:       substituteAndHighlight(effectiveBody, data),
      overrideActive: !!overrideSubject || !!overrideBody
    }
  }, [selection, campaignSubject, campaignBodyHtml])

  // The full HTML document the iframe will render. Wraps the merged
  // body in the same thin shell the dispatch emits, so the preview
  // matches the inbox render. Computed before the early-return so
  // hook order is stable.
  const iframeSrcDoc = useMemo(() => {
    const body = computed?.bodyHtml || '<p style="color:#bfbfbf">(empty body)</p>'
    return wrapInThinShell(body, computed?.subject || '', bodySource)
  }, [computed?.bodyHtml, computed?.subject, bodySource])

  if (!selection) {
    return (
      <Card
        title='Preview'
        size='small'
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Empty
          description={
            <span>
              Click a recipient or team above to preview the actual email
              <br />
              that will be sent (with merge fields substituted).
            </span>
          }
        />
      </Card>
    )
  }

  if (!computed) return null

  // Cc shown = per-recipient/team Cc + campaign-level Cc (deduped, case-
  // insensitive). Bcc is campaign-level only. Mirrors the dispatch's step-9b
  // merge so the preview header matches what actually goes out.
  const seenCc = new Set<string>()
  const mergedCc = [...computed.ccAddrs, ...campaignCc].filter((a) => {
    const k = (a || '').trim().toLowerCase()
    if (!k || seenCc.has(k)) return false
    seenCc.add(k)
    return true
  })
  const mergedBcc = campaignBcc.filter((a) => (a || '').trim() !== '')

  return (
    <Card
      title='Preview'
      size='small'
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      bodyStyle={{ flex: 1, overflow: 'auto', padding: 12 }}
      extra={
        computed.overrideActive ? (
          <Tag color='blue'>Per-{selection.kind === 'team' ? 'team' : 'recipient'} override active</Tag>
        ) : (
          <Tag>Campaign default</Tag>
        )
      }
    >
      {/* From / To / Cc / Subject header strip — looks like an email client. */}
      <div
        style={{
          border:        '1px solid #f0f0f0',
          borderRadius:  6,
          padding:       '10px 12px',
          marginBottom:  10,
          background:    '#fafafa',
          fontSize:      12,
          lineHeight:    '1.7'
        }}
      >
        <div>
          <Text type='secondary'>To: </Text>
          <Text>{computed.toAddrs.join(', ') || '(none)'}</Text>
        </div>
        {mergedCc.length > 0 && (
          <div>
            <Text type='secondary'>Cc: </Text>
            <Text>{mergedCc.join(', ')}</Text>
          </div>
        )}
        {mergedBcc.length > 0 && (
          <div>
            <Text type='secondary'>Bcc: </Text>
            <Text>{mergedBcc.join(', ')}</Text>
          </div>
        )}
        <div>
          <Text type='secondary'>Subject: </Text>
          <Text strong>{computed.subject || '(empty)'}</Text>
        </div>
      </div>

      {/*
        iframe srcDoc gives the body its own document + CSS scope +
        viewport calcs — much closer to inbox-client behaviour than
        the shadow-DOM approach we used before. The sandbox attr
        disables scripts / forms / top-nav inside the iframe so the
        preview can't be turned into an attack surface even if a
        sanitizer bug let HTML through. allow-same-origin is needed
        for the onLoad handler to read the iframe document's
        scrollHeight for auto-resize.
      */}
      <iframe
        ref={iframeRef}
        title='Email body preview'
        srcDoc={iframeSrcDoc}
        onLoad={handleIframeLoad}
        sandbox='allow-same-origin'
        style={{
          width:        '100%',
          height:       iframeHeight,
          border:       '1px solid #f0f0f0',
          borderRadius: 6,
          background:   '#ffffff',
          display:      'block'
        }}
      />

      <div style={{ marginTop: 10, fontSize: 11, color: '#8c8c8c' }}>
        <Space size={6} wrap>
          <Tag color='gold' style={{ margin: 0 }}>highlighted</Tag>
          <span>= merge field substituted at send time</span>
          <Tag color='red' style={{ margin: 0 }}>red dashed</Tag>
          <span>= placeholder existed but resolved to empty</span>
        </Space>
      </div>
    </Card>
  )
}
